import type {
  CmmdOrgProviderPolicy,
  CmmdOrgProviderPolicyWallet,
  ProviderKind,
} from "@cmmd-forge/contracts";

/**
 * Fail-closed offline defaults for org provider policy projection.
 * CMMD is the source of truth; Forge Settings/picker project this shape.
 * Do not widen offerability here without an explicit CMMD policy response.
 */
export const DEFAULT_ORG_PROVIDER_POLICY: CmmdOrgProviderPolicy = {
  providers: {
    codex: true,
    claude: false,
    cmmd: true,
  },
  // Fail closed: no provider is approved for metered egress until CMMD says so.
  meteredEgressApproved: {
    codex: false,
    claude: false,
    cmmd: false,
  },
  allowedWallets: ["personal"],
  updatedAt: "1970-01-01T00:00:00.000Z",
};

type OrgProviderPolicyProviders = CmmdOrgProviderPolicy["providers"];

/** Settings provider keys that map onto CMMD policy providers. */
export type SettingsProviderKind = ProviderKind;

/**
 * Normalise an org policy onto the fail-closed defaults.
 *
 * This MERGES rather than substitutes, and that is load-bearing. `policy` comes
 * from `getOrgProviderPolicy()`, which is `request<CmmdOrgProviderPolicy>(...)`
 * — a raw generic cast over an HTTP body with no runtime validation. A truthy
 * but incomplete payload (a partial, an error envelope, `{}`) therefore reaches
 * this function typed as a full policy while `providers` is `undefined`.
 *
 * The previous `policy ?? DEFAULT` only guarded null/undefined, so such a
 * payload passed straight through and `isOfferableProvider` threw
 * `Cannot read properties of undefined (reading 'codex')` — INSIDE a React
 * render, which is a white screen, not a degraded picker.
 *
 * CMMD's own decoder already works this way ("Tolerate partial blobs … by
 * merging onto defaults", forge-org-provider-policy.service.ts). Both sides of
 * one contract now normalise identically instead of the client trusting a shape
 * the server is careful to repair.
 *
 * Unknown/malformed values fall back to the FAIL-CLOSED defaults — never to
 * "offer everything" — so a broken payload cannot silently widen offerability.
 */
export function resolveOrgProviderPolicy(
  policy: CmmdOrgProviderPolicy | null | undefined,
): CmmdOrgProviderPolicy {
  if (policy === null || policy === undefined) return DEFAULT_ORG_PROVIDER_POLICY;
  const providers = policy.providers as Partial<OrgProviderPolicyProviders> | undefined;
  const meteredEgressApproved = policy.meteredEgressApproved as
    | Partial<OrgProviderPolicyProviders>
    | undefined;
  const wallets = policy.allowedWallets;
  return {
    providers: { ...DEFAULT_ORG_PROVIDER_POLICY.providers, ...providers },
    meteredEgressApproved: {
      ...DEFAULT_ORG_PROVIDER_POLICY.meteredEgressApproved,
      ...meteredEgressApproved,
    },
    allowedWallets:
      Array.isArray(wallets) && wallets.length > 0
        ? wallets
        : DEFAULT_ORG_PROVIDER_POLICY.allowedWallets,
    updatedAt:
      typeof policy.updatedAt === "string"
        ? policy.updatedAt
        : DEFAULT_ORG_PROVIDER_POLICY.updatedAt,
  };
}

/**
 * Map Forge provider kinds onto CMMD org policy keys.
 * - CMMD `claude` ↔ Forge `claudeAgent`
 * - CMMD `cmmd` ↔ Forge `cmmd` / `apex`
 */
function policyKeyForProvider(provider: ProviderKind): keyof OrgProviderPolicyProviders | null {
  switch (provider) {
    case "codex":
      return "codex";
    case "claudeAgent":
      return "claude";
    case "cmmd":
    case "apex":
      return "cmmd";
    default:
      return null;
  }
}

/**
 * The providers the Provider Offer can name, in CMMD policy terms.
 *
 * The inverse of `policyKeyForProvider`, and the reason the picker rail has a
 * floor: these rows exist whether or not an Environment has published anything,
 * because the org's answer about them is a fact the Shell can state on its own.
 * `apex` is deliberately absent — it shares CMMD's policy key and its display
 * name, so it is one product row, not two.
 */
export const OFFERABLE_PROVIDER_KINDS: ReadonlyArray<ProviderKind> = [
  "codex",
  "claudeAgent",
  "cmmd",
];

/**
 * Why a provider is not being offered.
 *
 * An internal discriminant, not a copy key. Every user-facing sentence about a
 * withheld provider now comes from the one record in
 * `cmmd/providerIneligibilityCopy.ts`, keyed over the contract's four reasons;
 * a second map here is how "Coming soon." once covered a policy failure.
 */
export type ProviderOfferabilityReason = "org-disabled" | "policy-unavailable" | "unknown-provider";

/**
 * Offerability plus the reason it was withheld.
 *
 * A bare boolean cannot answer "why", and that is the whole defect: an org that
 * deliberately disabled a provider and a policy request that never landed both
 * resolve to `false`, so no surface can explain the empty picker.
 */
export type ProviderOfferability =
  | { readonly offered: true }
  | { readonly offered: false; readonly reason: ProviderOfferabilityReason };

/**
 * The org's answer for one provider, or `null` when we never learned it.
 *
 * `null` covers a missing policy AND a truthy-but-unusable one, because
 * `getOrgProviderPolicy()` is an unvalidated cast over an HTTP body and its
 * react-query hook yields `undefined` on failure. Both mean the same thing to a
 * caller: we do not know what the org wants.
 */
function orgAnswerForProvider(
  key: keyof OrgProviderPolicyProviders,
  policy: CmmdOrgProviderPolicy | null | undefined,
): boolean | null {
  if (policy === null || policy === undefined) return null;
  const providers = policy.providers as Partial<OrgProviderPolicyProviders> | undefined;
  if (providers === null || typeof providers !== "object") return null;
  const answer = providers[key];
  return typeof answer === "boolean" ? answer : null;
}

/**
 * Whether Forge may offer this provider, and if not, why.
 *
 * Offerability itself is unchanged and still fail-closed: when the org's answer
 * is unknown we fall back to `DEFAULT_ORG_PROVIDER_POLICY`, which offers Codex
 * and withholds Claude. That asymmetry is deliberate, and it is also why a
 * transient policy failure reads as a deliberate Codex-only configuration. The
 * reason now travels with the decision so callers can say which happened.
 */
export function resolveProviderOfferability(
  provider: ProviderKind,
  policy?: CmmdOrgProviderPolicy | null,
): ProviderOfferability {
  const key = policyKeyForProvider(provider);
  if (key === null) return { offered: false, reason: "unknown-provider" };

  const orgAnswer = orgAnswerForProvider(key, policy);
  if (orgAnswer === true) return { offered: true };
  if (orgAnswer === false) return { offered: false, reason: "org-disabled" };

  // Unknown. Fail closed onto the defaults, and name the uncertainty.
  return resolveOrgProviderPolicy(policy).providers[key] === true
    ? { offered: true }
    : { offered: false, reason: "policy-unavailable" };
}

/**
 * Whether Forge may offer this provider in picker / Settings “live product” under
 * the given (or fail-closed default) org policy.
 *
 * Delegates to `resolveProviderOfferability` so offerability has exactly ONE
 * predicate. Read and write seams that approximate this decision separately are
 * how the picker and Settings drifted apart.
 *
 * `providers.*` is offerability only — never a gate for per-user personal Connect.
 */
export function isOfferableProvider(
  provider: ProviderKind,
  policy?: CmmdOrgProviderPolicy | null,
): boolean {
  return resolveProviderOfferability(provider, policy).offered;
}

/**
 * Execution-surface offerability. `null` is an explicit standalone/local-runtime mode where
 * executable inventory and runtime auth are authoritative. `undefined` retains the fail-closed
 * CMMD-cloud fallback and must never be used to imply local-runtime authority.
 */
export function isProviderAllowedByExecutionPolicy(
  provider: ProviderKind,
  policy: CmmdOrgProviderPolicy | null | undefined,
): boolean {
  return resolveExecutionOfferability(provider, policy).offered;
}

/**
 * Execution-surface offerability WITH its reason, for surfaces that must render
 * a withheld provider rather than drop it.
 *
 * Preserves the `null` contract exactly: explicit standalone/local-runtime mode
 * defers to executable inventory and runtime auth, so everything is offered here
 * and the caller decides. `undefined` keeps the fail-closed CMMD-cloud fallback.
 */
export function resolveExecutionOfferability(
  provider: ProviderKind,
  policy: CmmdOrgProviderPolicy | null | undefined,
): ProviderOfferability {
  if (policy === null) return { offered: true };
  return resolveProviderOfferability(provider, policy);
}

export function isWalletAllowed(
  wallet: CmmdOrgProviderPolicyWallet,
  policy?: CmmdOrgProviderPolicy | null,
): boolean {
  return resolveOrgProviderPolicy(policy).allowedWallets.includes(wallet);
}

/**
 * Personal Claude/Codex Connect is per-userId and independent of `providers.*`.
 * When the personal wallet is allowed, members may link their own credentials
 * even if the org has not offered that provider in the picker.
 */
export function isPersonalProviderConnectAllowed(policy?: CmmdOrgProviderPolicy | null): boolean {
  return isWalletAllowed("personal", policy);
}

function isPersonalConnectSettingsProvider(provider: SettingsProviderKind): boolean {
  return provider === "claudeAgent" || provider === "codex";
}

/**
 * Settings card visibility.
 * - Claude: show when personal Connect is allowed OR when offerable (do not
 *   hide Connect behind `providers.claude === false`).
 * - Other MVP cards remain listed (coming-soon when not offerable).
 */
export function isProviderSettingsCardVisible(
  provider: SettingsProviderKind,
  policy?: CmmdOrgProviderPolicy | null,
): boolean {
  if (provider === "claudeAgent") {
    return isPersonalProviderConnectAllowed(policy) || isOfferableProvider("claudeAgent", policy);
  }
  return true;
}

/**
 * Settings “Coming soon” badge — not the picker gate.
 * Personal Connect surfaces (Claude/Codex) stay live when the personal wallet
 * is allowed, even if `providers.*` keeps them off the model picker.
 */
export function isSettingsProviderComingSoon(input: {
  readonly provider: SettingsProviderKind;
  readonly policy?: CmmdOrgProviderPolicy | null;
  readonly isDebugDisclosureEnabled: boolean;
}): boolean {
  if (input.isDebugDisclosureEnabled) return false;
  if (
    isPersonalConnectSettingsProvider(input.provider) &&
    isPersonalProviderConnectAllowed(input.policy)
  ) {
    return false;
  }
  const provider: ProviderKind =
    input.provider === "apex" ? "apex" : (input.provider as ProviderKind);
  return !isOfferableProvider(provider, input.policy);
}
